import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { IconType } from 'react-icons';
import { toast } from 'react-toastify';
import { FiCheckCircle } from 'react-icons/fi';
import { AnySubscriptionType, BusinessSubscriptionType, SubscriptionType } from '../../../types/misc';
import {
  baseSubscriptionPrices,
  variableSubscriptionPrices,
  subscriptionFeatures,
  isSubscriptionGreaterThanOrEqual,
  isSubscriptionGreaterThan,
  shouldShowTrialBanner,
  FREE_MEMBERS_INCLUDED,
} from '../../../utils/subscriptionUtils';
import {
  businessBaseSubscriptionPrices,
  businessVariableSubscriptionPrices,
  businessSubscriptionFeatures,
  isBusinessSubscriptionGreaterThan,
  isBusinessSubscriptionGreaterThanOrEqual,
} from '../../../utils/BusinessFeatureUtils';
import CheckoutForm from '../../components/forms/CheckoutForm';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import useActiveSubscription from '../../../hooks/community/useActiveSubscription';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import useActiveBusinessSubscription from '../../../hooks/business/useActiveBusinessSubscription';
import Button from '../../components/buttons/Button';
import { stripeAppearance } from '../../../constants';
import { subscriptionService } from '../../../services/community/subscriptionService';
import { businessSubscriptionService } from '../../../services/business/subscriptionService';
import TrialBanner from '../../components/misc/TrialBanner';

const STRIPE_KEY: string = process.env.REACT_APP_STRIPE_KEY as string;
const stripe = loadStripe(STRIPE_KEY);

export default function UpgradeSubscriptionPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: '' });
  const location = useLocation();
  const navigate = useNavigate();

  const subscriptionType = location.state?.subscriptionType as AnySubscriptionType | undefined;
  const isRenewal = Boolean(location.state?.isRenewal);
  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';
  const [changePaymentMode, setChangePaymentMode] = useState<boolean>(
    Boolean(location.state?.changePaymentMode),
  );

  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const activeCommunitySubscription = useActiveSubscription();
  const activeBusinessSubscription = useActiveBusinessSubscription();

  const entity = isBusiness ? business : community;
  const activeSubscription: AnySubscriptionType = isBusiness
    ? activeBusinessSubscription
    : activeCommunitySubscription;
  const subService = isBusiness ? businessSubscriptionService : subscriptionService;

  const [userCount, setUserCount] = useState(0);

  const isOnTrial = !!entity?.trialEndDate && new Date(entity.trialEndDate) > new Date();

  useEffect(() => {
    subService.getSubscriptionUsage().then((usage) => {
      const count = typeof usage === 'number' ? usage : usage.memberCount;
      setUserCount(count);
    });
  }, [entity]);

  const getBasePrice = (type: AnySubscriptionType) =>
    isBusiness
      ? (businessBaseSubscriptionPrices as Record<string, number>)[type] ?? 0
      : baseSubscriptionPrices[type as SubscriptionType] ?? 0;

  const getVariablePrice = (type: AnySubscriptionType) =>
    isBusiness
      ? (businessVariableSubscriptionPrices as Record<string, number>)[type] ?? 0
      : variableSubscriptionPrices[type as SubscriptionType] ?? 0;

  const calculateTotal = (type: AnySubscriptionType, memberCount: number) => {
    const extraMembers = Math.max(0, memberCount - FREE_MEMBERS_INCLUDED);
    return getBasePrice(type) + extraMembers * getVariablePrice(type);
  };

  if (!subscriptionType) return <></>;

  const upgradeRoute = isBusiness ? '/subscription/upgrade' : '/subscription/upgrade';
  const basePrice = getBasePrice(subscriptionType);
  const variablePrice = getVariablePrice(subscriptionType);
  const extraMemberCost = userCount > 25 ? (userCount - 25) * variablePrice : 0;
  const features = isBusiness
    ? businessSubscriptionFeatures.filter((f) => (f as Record<string, unknown>)[subscriptionType])
    : subscriptionFeatures.filter((f) => (f as Record<string, unknown>)[subscriptionType]);

  return (
    <>
      {entity && shouldShowTrialBanner(entity) && (
        <TrialBanner
          trialEndDate={new Date(entity.trialEndDate!)}
          plan={entity.trialType!}
          showPaymentSetup={false}
          onPaymentSetup={() =>
            navigate(upgradeRoute, {
              state: { changePaymentMode: false, subscriptionType: activeSubscription, isBusiness },
            })
          }
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left column – plan summary */}
        <div className="space-y-6">
          <div className="rounded-3xl bg-white shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-primary-900">
                {isRenewal
                  ? t('page.subscriptions.upgrade.renewalTitle')
                  : t('page.subscriptions.upgrade.title')}
              </h1>
            </div>

            {!changePaymentMode && (
              <div className="mb-8">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="rounded-2xl p-4 bg-secondary-200">
                      <h3 className="text-lg font-semibold text-primary-900">
                        {String(activeSubscription)
                          .replace('_', ' ')
                          .toLowerCase()
                          .replace(/^\w/, (c) => c.toUpperCase())}{' '}
                        {isOnTrial ? `(${t('page.manageSubscription.freeTrial')})` : ''}
                      </h3>
                      <p className="text-sm text-primary-900 opacity-90">
                        {t('page.subscriptions.upgrade.current')}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <FaArrowRight className="h-6 w-6 text-primary-900" />
                  </div>
                  <div className="flex-1">
                    <div className="rounded-2xl p-4 bg-secondary-200">
                      <h3 className="text-lg font-semibold text-primary-900">
                        {String(subscriptionType)
                          .replace('_', ' ')
                          .toLowerCase()
                          .replace(/^\w/, (c) => c.toUpperCase())}
                      </h3>
                      <p className="text-sm text-primary-900 opacity-90">
                        {t('page.subscriptions.upgrade.upgrading')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing breakdown */}
            <div className="rounded-2xl bg-secondary-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-primary-900 mb-4">
                {t('page.subscriptions.upgrade.pricing')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-primary-900">
                    {t('page.subscriptions.upgrade.basePrice')}
                  </span>
                  <span className="font-semibold text-primary-900">
                    €{basePrice.toFixed(2)}/{t('general.month')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-primary-900">
                    {t('page.subscriptions.upgrade.extraMembers', {
                      count: userCount > 25 ? userCount - 25 : 0,
                    })}
                  </span>
                  <span className="font-semibold text-primary-900">
                    €{extraMemberCost.toFixed(2)}/{t('general.month')}
                  </span>
                </div>
                <div className="border-t border-primary-300 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary-900">
                      {t('page.subscriptions.upgrade.total')}
                    </span>
                    <span className="text-xl font-bold text-primary-900">
                      €{calculateTotal(subscriptionType, userCount).toFixed(2)}/
                      {t('general.month')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-primary-900 mb-4">
                {t('page.subscriptions.upgrade.benefits')}
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {features.map((feature) => (
                  <div key={feature.i18nKey} className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <FiCheckCircle className="h-5 w-5 text-primary-300" />
                    </div>
                    <span className="text-primary-900">
                      {t(`page.subscriptions.${feature.i18nKey}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column – payment */}
        <div className="space-y-6">
          <div className="rounded-3xl bg-white shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-primary-900 mb-6">
              {isOnTrial
                ? t('page.subscriptions.upgrade.setupPayment')
                : t('page.subscriptions.upgrade.paymentDetails')}
            </h2>

            {isOnTrial && (
              <ConditionListItem
                icon={FiCheckCircle}
                text={t('page.subscriptions.upgrade.noChargeUntilTrial')}
              />
            )}
            {subscriptionType !== SubscriptionType.FREE && (
              <>
                <ConditionListItem
                  icon={FiCheckCircle}
                  text={t('page.subscriptions.upgrade.subscriptionDetails')}
                />
                <ConditionListItem
                  icon={FiCheckCircle}
                  text={t('page.subscriptions.upgrade.cancelPolicy')}
                />
              </>
            )}

            <PaymentForm
              subscriptionType={subscriptionType}
              activeSubscription={activeSubscription}
              isOnTrial={isOnTrial}
              isBusiness={isBusiness}
              changePaymentMode={changePaymentMode}
              setChangePaymentMode={setChangePaymentMode}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function ConditionListItem({ icon: Icon, text }: { icon: IconType; text: string }): JSX.Element {
  return (
    <li className="flex items-center gap-1.5 mb-2">
      <Icon className="mt-0.5 shrink-0 self-start text-xl" />
      {text}
    </li>
  );
}

type PaymentFormProps = {
  subscriptionType: AnySubscriptionType;
  activeSubscription: AnySubscriptionType;
  isOnTrial: boolean;
  isBusiness: boolean;
  changePaymentMode: boolean;
  setChangePaymentMode: React.Dispatch<React.SetStateAction<boolean>>;
};

function PaymentForm({
  subscriptionType,
  activeSubscription,
  isOnTrial,
  isBusiness,
  changePaymentMode,
  setChangePaymentMode,
}: PaymentFormProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const entity = isBusiness ? business : community;

  const [disabledForm, setDisabledForm] = useState(false);
  const [agreedWithTerms, setAgreedWithTerms] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);

  const subService = isBusiness ? businessSubscriptionService : subscriptionService;
  const manageRoute = isBusiness ? '/business/subscription' : '/subscription/manage';
  const returnPath = isBusiness ? '/business/payment/completion/' : undefined;

  const isUpgrade = isBusiness
    ? isBusinessSubscriptionGreaterThan(
        subscriptionType as BusinessSubscriptionType,
        activeSubscription as BusinessSubscriptionType,
      )
    : isSubscriptionGreaterThan(
        subscriptionType as SubscriptionType,
        activeSubscription as SubscriptionType,
      );

  const isDowngrade = isBusiness
    ? isBusinessSubscriptionGreaterThanOrEqual(
        activeSubscription as BusinessSubscriptionType,
        subscriptionType as BusinessSubscriptionType,
      )
    : isSubscriptionGreaterThanOrEqual(
        activeSubscription as SubscriptionType,
        subscriptionType as SubscriptionType,
      );

  const isCancel = !isBusiness && subscriptionType === SubscriptionType.FREE;

  const needsPaymentSetup = !entity?.subscription?.type || isOnTrial || changePaymentMode;

  useEffect(() => {
    if (needsPaymentSetup) {
      subService
        .prepareSubscription(subscriptionType as never)
        .then(({ status, clientSecret: cs, customerId: cid }) => {
          if (status === 'subscription_already_active') setChangePaymentMode(true);
          setClientSecret(cs);
          setCustomerId(cid);
        });
    }
  }, [entity]);

  const options: StripeElementsOptions = {
    appearance: { ...stripeAppearance } as StripeElementsOptions['appearance'],
    clientSecret,
  };

  const changeSubscription = () => {
    subService
      .changeSubscription(subscriptionType as never)
      .then(() => navigate(manageRoute))
      .catch(() => toast.error(t('page.subscriptions.upgrade.error')));
  };

  const cancelSubscription = () => {
    subService
      .cancelSubscription()
      .then(() => navigate(manageRoute))
      .catch(() => toast.error(t('page.subscriptions.upgrade.error')));
  };

  return (
    <div className="space-y-6 mt-4">
      {needsPaymentSetup ? (
        clientSecret && stripe ? (
          <Elements options={options} stripe={stripe}>
            {!changePaymentMode ? (
              <CheckoutForm
                clientSecret={clientSecret}
                disabled={disabledForm}
                setDisabled={setDisabledForm}
                intentType="setup"
                onSubmit={() => {}}
                returnPath={returnPath}
                redirectQueryParams={`?customerId=${customerId}&status=loading&type=setup`}
              />
            ) : (
              <CheckoutForm
                clientSecret={clientSecret}
                disabled={disabledForm}
                setDisabled={setDisabledForm}
                intentType="setupChange"
                onSubmit={() => {}}
                returnPath={returnPath}
                redirectQueryParams={`?customerId=${customerId}&status=loading&type=setup`}
              />
            )}
          </Elements>
        ) : (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-900" />
          </div>
        )
      ) : isUpgrade ? (
        <>
          <TermsCheckbox
            label={t('page.subscriptions.upgrade.acceptPayment')}
            agreedWithTerms={agreedWithTerms}
            setAgreedWithTerms={setAgreedWithTerms}
          />
          <ActionButton
            onClick={changeSubscription}
            label={t('page.subscriptions.upgrade.confirmUpgrade')}
            disabled={!agreedWithTerms}
          />
        </>
      ) : isDowngrade ? (
        <>
          <TermsCheckbox
            label={t('page.subscriptions.upgrade.accept')}
            agreedWithTerms={agreedWithTerms}
            setAgreedWithTerms={setAgreedWithTerms}
          />
          <ActionButton
            onClick={isCancel ? cancelSubscription : changeSubscription}
            label={t('page.subscriptions.upgrade.confirmChange')}
            disabled={!agreedWithTerms}
          />
        </>
      ) : null}
    </div>
  );
}

function TermsCheckbox({
  label,
  agreedWithTerms,
  setAgreedWithTerms,
}: {
  label: string;
  agreedWithTerms: boolean;
  setAgreedWithTerms: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <div className="rounded-2xl bg-secondary-200 p-4">
      <div className="flex items-center gap-3">
        <input
          id="terms"
          type="checkbox"
          checked={agreedWithTerms}
          onChange={() => setAgreedWithTerms((prev) => !prev)}
          className="h-5 w-5 rounded border-2 border-primary-300 text-primary-300 focus:ring-primary-300"
        />
        <label htmlFor="terms" className="text-primary-900 font-medium cursor-pointer flex-1">
          {label}
        </label>
      </div>
    </div>
  );
}

function ActionButton({
  onClick,
  label,
  disabled,
}: {
  onClick: () => void;
  label: string;
  disabled: boolean;
}) {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      variant="primary"
      className="w-full justify-center py-4 text-lg font-semibold"
    >
      {label}
    </Button>
  );
}
